import {
  Button,
  ButtonVariant,
  Toolbar,
  ToolbarItem,
  ToolbarContent,
  ToolbarFilter,
  ToolbarToggleGroup,
  ToolbarGroup,
  Select,
  SelectVariant,
  SelectOption,
  Dropdown,
  DropdownItem,
  DropdownPosition,
  DropdownToggle,
  InputGroup,
  TextInput,
  Pagination,
  SelectOptionObject,
} from '@patternfly/react-core'
import { SyncAltIcon, FilterIcon } from '@patternfly/react-icons'
import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'

interface Props {
  patientName?: string
  onPatientNameUpdated: (name?: string) => void
  mrn?: string
  onMrnUpdated: (mrn?: string) => void
  isDeliverable?: boolean
  onIsDeliverableUpdated: (isDeliverable?: boolean) => void
  statusFilter?: string
  onStatusFilterUpdated?: (statusFilter?: string) => void
  onRefresh: () => void
  pagination: {
    pageNumber: number
    recordsPerPage: number
    totalRecords: number
    onPageChange: (page: number) => void
    onPerPageSelect: (perPage: number) => void
  }
}

const isDeliverableChips = ['Deliverable']
const isUndeliverableChips = ['Undeliverable']

const statusLabels = {
  cancelled: 'Cancelled',
  completed: 'Completed',
  delivered: 'Delivered',
  pending: 'Pending',
  expired: 'Expired',
  'in-progress': 'In-Progress',
  removed: 'Removed',
}

export const ConfiguredInteractionsToolbar = ({
  patientName,
  onPatientNameUpdated,
  mrn,
  onMrnUpdated,
  isDeliverable,
  onIsDeliverableUpdated,
  statusFilter,
  onStatusFilterUpdated,
  onRefresh,
  pagination,
}: Props) => {
  const {
    pageNumber,
    recordsPerPage,
    totalRecords,
    onPageChange,
    onPerPageSelect,
  } = pagination

  const patientNameChips = useMemo(() => {
    return patientName ? [patientName] : []
  }, [patientName])

  const mrnChips = useMemo(() => {
    return mrn ? [mrn] : []
  }, [mrn])

  const deliverableChips =
    isDeliverable === undefined
      ? []
      : isDeliverable
      ? isDeliverableChips
      : isUndeliverableChips

  const deliverableSelection =
    isDeliverable === undefined
      ? undefined
      : isDeliverable
      ? 'Deliverable'
      : 'Undeliverable'

  const statusChips = useMemo(() => {
    return statusFilter ? [statusLabels[statusFilter]] : []
  }, [statusFilter])

  const [currentCategory, setCurrentCategory] = useState<string>('patient_name')
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false)
  const [isDeliverableDropdownOpen, setIsDeliverableDropdownOpen] = useState(
    false,
  )
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false)

  const onDeliverableToggle = useCallback((isOpen: boolean) => {
    setIsDeliverableDropdownOpen(isOpen)
  }, [])

  const onStatusToggle = useCallback((isOpen: boolean) => {
    setIsStatusDropdownOpen(isOpen)
  }, [])

  const onCategoryToggle = useCallback(() => {
    setIsCategoryDropdownOpen((open) => !open)
  }, [])

  const onCategorySelect = useCallback((event: React.SyntheticEvent) => {
    if (event.target instanceof HTMLAnchorElement) {
      setCurrentCategory(event.target.getAttribute('value') ?? 'patient_name')
      setIsCategoryDropdownOpen(false)
    }
  }, [])

  const onClearAllFilters = useCallback(() => {
    onPatientNameUpdated(undefined)
    onMrnUpdated(undefined)
    onIsDeliverableUpdated(undefined)
    onStatusFilterUpdated(undefined)
  }, [
    onPatientNameUpdated,
    onMrnUpdated,
    onIsDeliverableUpdated,
    onStatusFilterUpdated,
  ])

  const handlePatientNameChange = useCallback(
    (name: string) => {
      onPatientNameUpdated(name)
    },
    [onPatientNameUpdated],
  )

  const handleMrnChange = useCallback(
    (mrn: string) => {
      onMrnUpdated(mrn)
    },
    [onMrnUpdated],
  )

  const handleIsDeliverableChange = useCallback(
    (
      _event: React.MouseEvent | React.ChangeEvent,
      selection: string | SelectOptionObject,
    ) => {
      if (onIsDeliverableUpdated) {
        if (selection === 'Deliverable') {
          onIsDeliverableUpdated(true)
        } else if (selection === 'Undeliverable') {
          onIsDeliverableUpdated(false)
        }
        setIsDeliverableDropdownOpen(false)
      }
    },
    [onIsDeliverableUpdated],
  )

  const handleStatusChange = useCallback(
    (
      _event: React.MouseEvent | React.ChangeEvent,
      selection: string | SelectOptionObject,
    ) => {
      if (onStatusFilterUpdated) {
        let newStatus
        if (typeof selection === 'string') {
          newStatus = selection
        } else {
          newStatus = selection.toString()
        }
        const statusEntry = Object.entries(statusLabels).find(
          (e) => e[1] === newStatus,
        )

        onStatusFilterUpdated(statusEntry[0])
        setIsStatusDropdownOpen(false)
      }
    },
    [onStatusFilterUpdated],
  )

  const handleDeletePatientNameChip = useCallback(() => {
    onPatientNameUpdated(undefined)
  }, [onPatientNameUpdated])

  const handleDeleteMrnChip = useCallback(() => {
    onMrnUpdated(undefined)
  }, [onMrnUpdated])

  const handleDeleteDeliverable = useCallback(() => {
    onIsDeliverableUpdated?.(undefined)
  }, [onIsDeliverableUpdated])

  const handleDeleteStatus = useCallback(() => {
    onStatusFilterUpdated?.(undefined)
  }, [onStatusFilterUpdated])

  const handleSetPage = useCallback(
    (_: React.MouseEvent | React.KeyboardEvent | MouseEvent, page: number) => {
      onPageChange(page)
    },
    [onPageChange],
  )

  const handlePerPageSelect = useCallback(
    (
      _: React.MouseEvent | React.KeyboardEvent | MouseEvent,
      perPage: number,
    ) => {
      onPerPageSelect(perPage)
    },
    [onPerPageSelect],
  )

  return (
    <Toolbar
      id="completed-interactions-toolbar"
      clearAllFilters={onClearAllFilters}
    >
      <ToolbarContent>
        <ToolbarToggleGroup toggleIcon={<FilterIcon />} breakpoint="xl">
          <ToolbarGroup variant="filter-group">
            <ToolbarItem>
              <Dropdown
                onSelect={onCategorySelect}
                position={DropdownPosition.left}
                toggle={
                  <DropdownToggle onToggle={onCategoryToggle}>
                    <FilterIcon />{' '}
                    {currentCategory === 'patient_name'
                      ? 'Patient Name'
                      : currentCategory === 'mrn'
                      ? 'MRN'
                      : currentCategory === 'deliverable'
                      ? 'Deliverable'
                      : 'Status'}
                  </DropdownToggle>
                }
                isOpen={isCategoryDropdownOpen}
                dropdownItems={[
                  <DropdownItem key="patient_name" value="patient_name">
                    Patient Name
                  </DropdownItem>,
                  <DropdownItem key="mrn" value="mrn">
                    MRN
                  </DropdownItem>,
                  <DropdownItem key="deliverable" value="deliverable">
                    Communication Status
                  </DropdownItem>,
                  <DropdownItem key="status" value="status">
                    Status
                  </DropdownItem>,
                ]}
              />
            </ToolbarItem>

            <ToolbarFilter
              chips={patientNameChips}
              deleteChip={handleDeletePatientNameChip}
              categoryName="Patient Name"
              showToolbarItem={currentCategory === 'patient_name'}
            >
              <InputGroup>
                <TextInput
                  name="patientNameInput"
                  id="patientNameInput"
                  type="search"
                  aria-label="Filter by patient name"
                  value={patientName || ''}
                  onChange={handlePatientNameChange}
                  placeholder="Filter by Patient Name..."
                />
              </InputGroup>
            </ToolbarFilter>

            <ToolbarFilter
              chips={mrnChips}
              deleteChip={handleDeleteMrnChip}
              categoryName="MRN"
              showToolbarItem={currentCategory === 'mrn'}
            >
              <InputGroup>
                <TextInput
                  name="mrnInput"
                  id="mrnInput"
                  type="search"
                  aria-label="Filter by MRN"
                  value={mrn || ''}
                  onChange={handleMrnChange}
                  placeholder="Filter by MRN..."
                />
              </InputGroup>
            </ToolbarFilter>
            <ToolbarFilter
              chips={deliverableChips}
              deleteChip={handleDeleteDeliverable}
              categoryName={'Communication Status'}
              showToolbarItem={currentCategory === 'deliverable'}
            >
              <Select
                variant={SelectVariant.single}
                aria-label={'Communication Status'}
                onToggle={onDeliverableToggle}
                onSelect={handleIsDeliverableChange}
                selections={deliverableSelection}
                isOpen={isDeliverableDropdownOpen}
                placeholderText="Filter by Communication Status..."
              >
                <SelectOption key="deliverable" value="Deliverable" />
                <SelectOption key="undeliverable" value="Undeliverable" />
              </Select>
            </ToolbarFilter>

            <ToolbarFilter
              chips={statusChips}
              deleteChip={handleDeleteStatus}
              categoryName={'Status'}
              showToolbarItem={currentCategory === 'status'}
            >
              <Select
                variant={SelectVariant.single}
                aria-label={'Status'}
                onToggle={onStatusToggle}
                onSelect={handleStatusChange}
                selections={statusFilter ? [statusFilter] : []}
                isOpen={isStatusDropdownOpen}
                placeholderText="Filter by status..."
              >
                <SelectOption key="pending" value={statusLabels['pending']} />
                <SelectOption
                  key="delivered"
                  value={statusLabels['delivered']}
                />
                <SelectOption
                  key="in-progress"
                  value={statusLabels['in-progress']}
                />
                <SelectOption
                  key="completed"
                  value={statusLabels['completed']}
                />
                <SelectOption
                  key="cancelled"
                  value={statusLabels['cancelled']}
                />
                <SelectOption key="removed" value={statusLabels['removed']} />
                <SelectOption key="expired" value={statusLabels['expired']} />
              </Select>
            </ToolbarFilter>
          </ToolbarGroup>
        </ToolbarToggleGroup>

        <ToolbarItem>
          <Button variant={ButtonVariant.control} onClick={onRefresh}>
            <SyncAltIcon />
          </Button>
        </ToolbarItem>

        <ToolbarItem variant="pagination" alignment={{ default: 'alignRight' }}>
          <Pagination
            itemCount={totalRecords}
            page={pageNumber}
            perPage={recordsPerPage}
            onSetPage={handleSetPage}
            onPerPageSelect={handlePerPageSelect}
          />
        </ToolbarItem>
      </ToolbarContent>
    </Toolbar>
  )
}
